import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Text,
  Card,
  Badge,
  SearchInput,
  StatusDot,
  EmptyState,
  QueryChips,
  useToast,
} from '@teals/ui-web';
import {
  useGetAgentsQuery,
  useGetOrganisationQuery,
  type Agent,
} from '../../store/api';
import { useAuth } from '../../hooks';

type AgentScope = Agent['scope'];
type ViewMode = 'agent' | 'system';

const SCOPE_BADGES: Record<
  AgentScope,
  { variant: 'primary' | 'info' | 'warning' | 'neutral'; label: string }
> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  application: { variant: 'warning', label: 'Application' },
  system: { variant: 'neutral', label: 'System' },
};

const TYPE_BADGES: Record<
  string,
  { variant: 'primary' | 'info' | 'warning' | 'neutral'; label: string }
> = {
  management: { variant: 'warning', label: 'Management' },
  conversational: { variant: 'info', label: 'Conversational' },
  task: { variant: 'warning', label: 'Task' },
};

function typeKey(agent: Agent): string {
  return (agent.agentType || 'conversational').toLowerCase();
}

function isManagement(agent: Agent): boolean {
  return (
    typeKey(agent) === 'management' ||
    Boolean(agent.fleet?.agents && agent.fleet.agents.length > 0)
  );
}

function isSpecialist(agent: Agent): boolean {
  return Boolean(agent.membership?.parentAgentName) && !isManagement(agent);
}

function displayTitle(agent: Agent): string {
  if (agent.name && agent.name !== agent.agentName) return agent.name;
  return agent.name || agent.agentName;
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: 767px) {
    padding: 1.5rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const FiltersGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`;

const CreateAgentButton = styled(Button)`
  flex-shrink: 0;
`;

const SearchWrap = styled.div`
  flex: 0 1 280px;
  min-width: 180px;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 2rem 0.5rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: 0.875rem;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const ViewToggle = styled.div`
  display: inline-flex;
  padding: 3px;
  background: var(--bg-elevated, #f1f5f9);
  border: 1px solid var(--border);
  border-radius: 8px;
  gap: 2px;
`;

const ViewToggleBtn = styled.button<{ $active?: boolean }>`
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: inherit;
  color: ${({ $active }) =>
    $active ? 'var(--text-primary)' : 'var(--text-muted)'};
  background: ${({ $active }) =>
    $active ? 'var(--bg-surface)' : 'transparent'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-sm)' : 'none')};
`;

const HintRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HintChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  font-size: 0.8125rem;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const OnboardingCard = styled(Card)`
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OnboardingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const DismissButton = styled.button`
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.25rem;
`;

const SystemBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent) 25%, transparent);
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const AgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  align-items: stretch;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const AgentCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
  min-width: 0;
  cursor: pointer;
  border: 1px solid var(--border);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
`;

const CardTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  flex-shrink: 0;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(1.5em * 2);
`;

const MembershipChip = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  border: none;
  background: color-mix(in srgb, var(--info, #3b82f6) 12%, transparent);
  color: var(--info, #3b82f6);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

const SystemCta = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--accent);
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border);
`;

const MetaText = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const ModelBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: var(--bg-elevated, #f1f5f9);
  color: var(--text-secondary);
`;

const DividerDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--text-muted);
  flex-shrink: 0;
`;

const LoadingState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { show } = useToast();
  const { user } = useAuth();
  const onboardingMode = searchParams.get('onboarding');
  const orgId = user?.organizationId || '';
  const dismissKey = orgId ? `lumina.agents.onboarding.dismissed.${orgId}` : '';
  const [onboardingOpen, setOnboardingOpen] = useState(
    () => onboardingMode === 'owner' || onboardingMode === 'member'
  );

  const { data: organisation } = useGetOrganisationQuery(orgId, { skip: !orgId });
  const orgName = organisation?.name || 'your organisation';
  const memberToastShown = useRef(false);

  useEffect(() => {
    if (dismissKey && localStorage.getItem(dismissKey) === '1') {
      setOnboardingOpen(false);
    }
  }, [dismissKey]);

  useEffect(() => {
    if (onboardingMode !== 'member' || memberToastShown.current) {
      return;
    }
    memberToastShown.current = true;
    show({
      message: `You joined ${orgName} as a member`,
      type: 'success',
    });
  }, [onboardingMode, orgName, show]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [versionFilter, setVersionFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'updated'>('name');
  const [viewMode, setViewMode] = useState<ViewMode>('agent');
  const [systemFilterId, setSystemFilterId] = useState<string | null>(null);

  const { data: agents = [], isLoading, isError } = useGetAgentsQuery(
    { orgId },
    { skip: !orgId },
  );

  const agentsById = useMemo(() => {
    const map = new Map<string, Agent>();
    agents.forEach((a) => {
      map.set(a.id, a);
      map.set(a.agentName, a);
    });
    return map;
  }, [agents]);

  const managementAgents = useMemo(
    () => agents.filter((a) => isManagement(a)),
    [agents],
  );

  const applySystemFilter = (managerIdOrName: string) => {
    const manager = agentsById.get(managerIdOrName);
    if (!manager) return;
    setViewMode('agent');
    setSystemFilterId(manager.id);
    setTypeFilter('');
  };

  const clearSystemFilter = () => setSystemFilterId(null);

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();

    const matchedSystems = q
      ? managementAgents.filter(
          (m) =>
            m.name.toLowerCase().includes(q) ||
            m.agentName.toLowerCase().includes(q) ||
            displayTitle(m).toLowerCase().includes(q),
        )
      : [];

    const systemExpandedIds = new Set<string>();
    matchedSystems.forEach((m) => {
      systemExpandedIds.add(m.id);
      (m.fleet?.agents || []).forEach((member) => {
        const found = agentsById.get(member.id || member.agentName);
        if (found) systemExpandedIds.add(found.id);
      });
    });

    let list = agents.filter((agent) => {
      if (viewMode === 'system') {
        if (isSpecialist(agent)) return false;
      }

      if (systemFilterId && viewMode === 'agent') {
        const manager = agentsById.get(systemFilterId);
        if (!manager) return false;
        const memberIds = new Set(
          (manager.fleet?.agents || []).map((m) => m.id || m.agentName),
        );
        const inFleet =
          agent.id === manager.id ||
          memberIds.has(agent.id) ||
          memberIds.has(agent.agentName);
        if (!inFleet) return false;
      }

      const matchesSearch =
        !q ||
        agent.name.toLowerCase().includes(q) ||
        agent.description.toLowerCase().includes(q) ||
        agent.agentName.toLowerCase().includes(q) ||
        systemExpandedIds.has(agent.id);

      const matchesType =
        !typeFilter || typeKey(agent) === typeFilter.toLowerCase();
      const matchesScope =
        scopeFilter === 'all' || agent.scope === scopeFilter;
      const matchesVersion =
        versionFilter === 'all' || agent.versionStatus === versionFilter;

      return matchesSearch && matchesType && matchesScope && matchesVersion;
    });

    return [...list].sort((a, b) => {
      if (sortBy === 'updated') {
        return (b.updatedAt || '').localeCompare(a.updatedAt || '');
      }
      return displayTitle(a).localeCompare(displayTitle(b));
    });
  }, [
    agents,
    agentsById,
    managementAgents,
    search,
    typeFilter,
    scopeFilter,
    versionFilter,
    sortBy,
    viewMode,
    systemFilterId,
  ]);

  const systemFilterManager = systemFilterId
    ? agentsById.get(systemFilterId)
    : undefined;

  const handleCreate = () => {
    navigate('/agents/new');
  };

  const setView = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'system' && systemFilterId) {
      clearSystemFilter();
    }
  };

  return (
    <Page>
      <Header>
        <HeaderCopy>
          <Text variant="h3">Agents</Text>
          <Subtitle>Build, test, and publish agents and agent systems</Subtitle>
        </HeaderCopy>
      </Header>

      {onboardingOpen && (onboardingMode === 'owner' || onboardingMode === 'member') && (
        <OnboardingCard padding={24} elevation="sm" aria-label="Getting started">
          <OnboardingHeader>
            <div>
              <Text variant="h4">
                {onboardingMode === 'member'
                  ? `You joined ${orgName}`
                  : `Welcome to ${orgName}`}
              </Text>
              <Subtitle>
                {onboardingMode === 'member'
                  ? 'You’re a member. Explore agents, then test in Playground. An owner can change your role.'
                  : 'Your organisation is ready. Three steps get you to first value — dismiss anytime.'}
              </Subtitle>
            </div>
            <DismissButton
              type="button"
              aria-label="Dismiss tips"
              onClick={() => {
                if (dismissKey) localStorage.setItem(dismissKey, '1');
                setOnboardingOpen(false);
                searchParams.delete('onboarding');
                searchParams.delete('joined');
                setSearchParams(searchParams, { replace: true });
              }}
            >
              ×
            </DismissButton>
          </OnboardingHeader>
          <QueryChips
            chips={
              onboardingMode === 'member'
                ? [
                    { label: '1. Open an agent' },
                    { label: '2. Test in Playground' },
                    { label: '3. Check Analytics' },
                  ]
                : [
                    { label: '1. Create an agent' },
                    { label: '2. Create an application' },
                    { label: '3. Invite teammates' },
                  ]
            }
            onSelect={(label) => {
              if (label.includes('agent') && label.includes('Create')) {
                navigate('/agents/new');
              } else if (label.includes('application')) {
                navigate('/applications');
              } else if (label.includes('Invite')) {
                navigate('/business');
              } else if (label.includes('Playground')) {
                navigate('/agents');
              } else if (label.includes('Analytics')) {
                navigate('/analytics');
              }
            }}
          />
        </OnboardingCard>
      )}

      <ActionBar>
        <FiltersGroup>
          <SearchWrap>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search agents…"
            />
          </SearchWrap>
          <FilterSelect
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter by type"
          >
            <option value="">Type: All</option>
            <option value="management">Management</option>
            <option value="conversational">Conversational</option>
            <option value="task">Task</option>
          </FilterSelect>
          <FilterSelect
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            aria-label="Filter by scope"
          >
            <option value="all">Scope: All</option>
            <option value="global">Global</option>
            <option value="organisation">Organisation</option>
          </FilterSelect>
          <FilterSelect
            value={versionFilter}
            onChange={(e) => setVersionFilter(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">Status: All</option>
            <option value="draft">Draft</option>
            <option value="sandbox">Sandbox</option>
            <option value="published">Published</option>
          </FilterSelect>
          <FilterSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'updated')}
            aria-label="Sort agents"
          >
            <option value="name">Sort: Name</option>
            <option value="updated">Sort: Last updated</option>
          </FilterSelect>
          <ViewToggle role="group" aria-label="Group agents">
            <ViewToggleBtn
              type="button"
              $active={viewMode === 'agent'}
              onClick={() => setView('agent')}
            >
              By agent
            </ViewToggleBtn>
            <ViewToggleBtn
              type="button"
              $active={viewMode === 'system'}
              onClick={() => setView('system')}
            >
              By system
            </ViewToggleBtn>
          </ViewToggle>
        </FiltersGroup>
        <CreateAgentButton variant="primary" onClick={handleCreate}>
          + Create Agent
        </CreateAgentButton>
      </ActionBar>

      {managementAgents.length > 0 && (
        <HintRow>
          <HintChip
            type="button"
            onClick={() =>
              navigate(
                `/agents/${encodeURIComponent(managementAgents[0].id)}?tab=playground`,
              )
            }
          >
            Test {displayTitle(managementAgents[0])} in Playground?
          </HintChip>
          <HintChip type="button" onClick={() => navigate('/applications')}>
            Associate with an application?
          </HintChip>
        </HintRow>
      )}

      {systemFilterManager && viewMode === 'agent' && (
        <SystemBanner role="status">
          <span>
            Showing system:{' '}
            <strong>{displayTitle(systemFilterManager)}</strong> — management
            agent + {systemFilterManager.fleet?.agents?.length ?? 0} specialists
          </span>
          <Button variant="ghost" size="sm" onClick={clearSystemFilter}>
            Clear filter
          </Button>
        </SystemBanner>
      )}

      {isLoading ? (
        <LoadingState>Loading agents…</LoadingState>
      ) : isError ? (
        <EmptyState
          title="Failed to load agents"
          description="Could not reach the portal agents API. Check your organisation context and try again."
        />
      ) : filteredAgents.length === 0 ? (
        <EmptyState
          title="No agents found"
          description={
            search ||
            typeFilter ||
            scopeFilter !== 'all' ||
            versionFilter !== 'all' ||
            systemFilterId
              ? 'No agents match your search or filters.'
              : 'Create your first organisation agent to get started.'
          }
          action={
            search ||
            typeFilter ||
            scopeFilter !== 'all' ||
            versionFilter !== 'all' ||
            systemFilterId ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setTypeFilter('');
                  setScopeFilter('all');
                  setVersionFilter('all');
                  clearSystemFilter();
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={handleCreate}>
                Create Agent
              </Button>
            )
          }
        />
      ) : (
        <AgentGrid>
          {filteredAgents.map((agent) => {
            const management = isManagement(agent);
            const typeCfg =
              TYPE_BADGES[typeKey(agent)] || TYPE_BADGES.conversational;
            const scopeCfg = SCOPE_BADGES[agent.scope];
            const fleetCount = agent.fleet?.agents?.length ?? 0;

            return (
              <AgentCard
                key={agent.id}
                padding={20}
                elevation="sm"
                onClick={() => {
                  if (viewMode === 'system' && management) {
                    applySystemFilter(agent.id);
                    return;
                  }
                  navigate(`/agents/${encodeURIComponent(agent.id)}`);
                }}
              >
                <CardHeader>
                  <CardTitle title={displayTitle(agent)}>
                    {displayTitle(agent)}
                  </CardTitle>
                  <BadgeRow>
                    <Badge variant={scopeCfg.variant} size="sm">
                      {scopeCfg.label}
                    </Badge>
                    <Badge variant={typeCfg.variant} size="sm">
                      {typeCfg.label}
                    </Badge>
                  </BadgeRow>
                </CardHeader>
                <CardDescription>
                  {agent.description ||
                    [agent.llmProvider, agent.llmModel]
                      .filter(Boolean)
                      .join(' · ') ||
                    'No description'}
                </CardDescription>
                {viewMode === 'agent' && agent.membership && (
                  <MembershipChip
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      applySystemFilter(
                        agent.membership?.parentId ||
                          agent.membership!.parentAgentName,
                      );
                    }}
                  >
                    In system · {agent.membership.parentAgentName}
                  </MembershipChip>
                )}
                {viewMode === 'system' && management && (
                  <SystemCta>View agents in this system →</SystemCta>
                )}
                <CardFooter>
                  <StatusDot
                    status={agent.versionStatus}
                    label={agent.versionStatus}
                  />
                  {management && fleetCount > 0 && (
                    <>
                      <DividerDot />
                      <MetaText>
                        {fleetCount} agent{fleetCount === 1 ? '' : 's'}
                      </MetaText>
                    </>
                  )}
                  {typeof agent.applicationCount === 'number' && (
                    <>
                      <DividerDot />
                      <MetaText>
                        {agent.applicationCount} app
                        {agent.applicationCount === 1 ? '' : 's'}
                      </MetaText>
                    </>
                  )}
                  {agent.llmModel && (
                    <>
                      <DividerDot />
                      <ModelBadge>{agent.llmModel}</ModelBadge>
                    </>
                  )}
                </CardFooter>
              </AgentCard>
            );
          })}
        </AgentGrid>
      )}
    </Page>
  );
};

export default Agents;
